/**
 * Dialog zum Speichern einer aktuellen Maßnahmen-Zeit als wiederverwendbare
 * Vorlage. Erlaubt die Eingabe eines Namens und die Auswahl der Sichtbarkeit
 * (Global oder nur dieser Standort).
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';

export const SaveTemplateDialog = ({
  open,
  onOpenChange,
  onSave,
  defaultName = '',
  locationId,
  locationName,
}) => {
  const [name, setName] = React.useState(defaultName);
  const [scope, setScope] = React.useState('global'); // 'global' | 'location'
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        location_id: scope === 'location' ? locationId : null,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle>Als Vorlage speichern</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tpl-name">Name</Label>
            <Input
              id="tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. BaE Hannover 2026"
              autoFocus
              data-testid="save-template-name"
            />
          </div>

          <div className="space-y-2">
            <Label>Sichtbarkeit</Label>
            <RadioGroup value={scope} onValueChange={setScope}>
              <div className="flex items-start gap-2 rounded-md border border-border p-3 hover:bg-accent/30 cursor-pointer"
                   onClick={() => setScope('global')}>
                <RadioGroupItem value="global" id="scope-global" data-testid="scope-global" />
                <div className="flex-1">
                  <Label htmlFor="scope-global" className="cursor-pointer font-medium">Global</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Für alle Standorte sichtbar
                  </p>
                </div>
              </div>
              <div
                className={`flex items-start gap-2 rounded-md border border-border p-3 ${
                  locationId ? 'hover:bg-accent/30 cursor-pointer' : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => locationId && setScope('location')}
              >
                <RadioGroupItem
                  value="location"
                  id="scope-location"
                  disabled={!locationId}
                  data-testid="scope-location"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="scope-location"
                    className={locationId ? 'cursor-pointer font-medium' : 'font-medium'}
                  >
                    Nur dieser Standort
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {locationId
                      ? `Nur sichtbar für „${locationName || 'Standort'}"`
                      : 'Maßnahme hat noch keinen Standort gewählt'}
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="save-template-cancel">
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={!name.trim() || saving} data-testid="save-template-confirm">
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTemplateDialog;
